// fetch-based API client. No axios per architecture anti-pattern list.

import { config } from '@/config.js';

class ApiError extends Error {
  constructor(status, code, message, retryAfter, details) {
    super(message || `API error ${code || status}`);
    this.name = 'ApiError';
    this.status = status;
    this.code = code;
    this.retryAfter = retryAfter;
    this.details = details;
  }
}

// Boot wiring sets this from main.js once the auth store + router are
// available. The client itself stays Vue-agnostic.
let on401Handler = null;
let isHandling401 = false;

export function setOn401Handler(fn) {
  on401Handler = typeof fn === 'function' ? fn : null;
}

// Some routes are expected to legitimately 401 (e.g., login itself, or the
// auth-bootstrap probe) and the global handler must not loop on them.
// `/auth/session` se llama al arrancar el SPA para rehidratar la sesión:
// un 401 ahí significa "no logueado", no "sesión expiró" — el router
// guard se encarga del redirect, así que NO disparamos el on401Handler.
const SKIP_401_PATHS = new Set(['/auth/login', '/auth/session']);

async function request(path, opts = {}) {
  const meta = await requestWithMeta(path, opts);
  return meta.body;
}

// requestWithMeta: variante que expone status + ETag además del body.
// Necesaria para endpoints que aprovechan If-None-Match → 304 (Story 4.1).
async function requestWithMeta(path, { method = 'GET', body, headers = {}, csrf, signal, skip401 = false } = {}) {
  const init = {
    method,
    credentials: 'include',
    headers: { Accept: 'application/json', ...headers },
    signal
  };
  if (body !== undefined && body !== null) {
    init.headers['Content-Type'] = 'application/json';
    init.body = JSON.stringify(body);
  }
  if (csrf) init.headers['X-XSRF-TOKEN'] = csrf;

  const res = await fetch(config.apiBaseUrl + path, init);
  const etag = res.headers.get('ETag') || res.headers.get('Etag') || null;

  // 304 Not Modified: no body, caller usa la copia anterior.
  if (res.status === 304) {
    return { status: 304, etag, body: null };
  }

  const text = await res.text();
  const json = text ? safeJson(text) : null;

  if (!res.ok) {
    const retry = Number(res.headers.get('Retry-After')) || undefined;

    if (
      res.status === 401
      && on401Handler
      && !isHandling401
      && !skip401
      && !SKIP_401_PATHS.has(path)
    ) {
      isHandling401 = true;
      try { on401Handler(); }
      finally { isHandling401 = false; }
    }

    // Backend envelope: { ok: false, error: "<code>", details?: {...} }.
    // Mantenemos compat: si error es objeto (legacy), extraemos code/message;
    // si es string (canonical), lo usamos directo.
    const code = typeof json?.error === 'string' ? json.error : json?.error?.code;
    const message = typeof json?.error === 'string'
      ? json?.message
      : json?.error?.message;
    // Story 4.7/4.8 VERSION_CONFLICT — envelope NO-canónica con
    // `current_version` y `current` en TOP-level (no en details). Los
    // copiamos a details para que el caller tenga una shape uniforme.
    let details = json?.details;
    if (code === 'version_conflict' && json) {
      details = {
        ...(details || {}),
        currentVersion: json.current_version,
        current: json.current,
      };
    }
    throw new ApiError(res.status, code, message, retry, details);
  }
  return { status: res.status, etag, body: json };
}

function safeJson(text) {
  try { return JSON.parse(text); } catch { return null; }
}

// Descarga CSV: fetch crudo (la respuesta es text/csv, no JSON) → blob +
// filename del Content-Disposition. GET con cookie de sesión; sin CSRF
// (método seguro). Lanza ApiError si la respuesta no es OK.
export async function fetchExport({ dataset, appId, window, signal } = {}) {
  const url = `${config.apiBaseUrl}/panels/export`
    + `?appId=${encodeURIComponent(appId)}`
    + `&window=${encodeURIComponent(window)}`
    + `&dataset=${encodeURIComponent(dataset)}`;
  const res = await fetch(url, { credentials: 'include', headers: { Accept: 'text/csv' }, signal });
  if (!res.ok) {
    throw new ApiError(res.status, null, `export failed (${res.status})`);
  }
  const disposition = res.headers.get('Content-Disposition') || '';
  const match = /filename="?([^"]+)"?/.exec(disposition);
  const filename = match ? match[1] : `${dataset}-${window}.csv`;
  return { blob: await res.blob(), filename };
}

export const api = {
  get: (path, opts) => request(path, { ...opts, method: 'GET' }),
  post: (path, body, opts) => request(path, { ...opts, method: 'POST', body }),
  put: (path, body, opts) => request(path, { ...opts, method: 'PUT', body }),
  delete: (path, opts) => request(path, { ...opts, method: 'DELETE' }),

  // Hero panel endpoints (Epic 2). Cada call recibe opts con
  // `signal` (AbortController) y `appId` ya resuelto por el caller.
  events: {
    pulse: ({ appId, signal }) =>
      request(`/panels/pulse?appId=${encodeURIComponent(appId)}`, { signal }),
    rageHotspots: ({ appId, window, signal }) =>
      request(`/panels/rage-hotspots?appId=${encodeURIComponent(appId)}&window=${encodeURIComponent(window)}`, { signal }),
    deadLeaderboard: ({ appId, window, signal }) =>
      request(`/panels/dead-leaderboard?appId=${encodeURIComponent(appId)}&window=${encodeURIComponent(window)}`, { signal }),
    pageviews: ({ appId, window, signal }) =>
      request(`/panels/pageviews?appId=${encodeURIComponent(appId)}&window=${encodeURIComponent(window)}`, { signal }),

    // Insight panel endpoints (KPI strip + rankings). Misma firma que
    // pageviews: todos respetan el time window seleccionado.
    summary: ({ appId, window, signal }) =>
      request(`/panels/summary?appId=${encodeURIComponent(appId)}&window=${encodeURIComponent(window)}`, { signal }),
    topPages: ({ appId, window, signal }) =>
      request(`/panels/top-pages?appId=${encodeURIComponent(appId)}&window=${encodeURIComponent(window)}`, { signal }),
    referrers: ({ appId, window, signal }) =>
      request(`/panels/referrers?appId=${encodeURIComponent(appId)}&window=${encodeURIComponent(window)}`, { signal }),
    tech: ({ appId, window, signal }) =>
      request(`/panels/tech?appId=${encodeURIComponent(appId)}&window=${encodeURIComponent(window)}`, { signal }),
    audience: ({ appId, window, signal }) =>
      request(`/panels/audience?appId=${encodeURIComponent(appId)}&window=${encodeURIComponent(window)}`, { signal }),
    customEvents: ({ appId, window, signal }) =>
      request(`/panels/custom-events?appId=${encodeURIComponent(appId)}&window=${encodeURIComponent(window)}`, { signal }),
    moduleUsage: ({ appId, window, signal }) =>
      request(`/panels/module-usage?appId=${encodeURIComponent(appId)}&window=${encodeURIComponent(window)}`, { signal }),
    anomalies: ({ appId, window, signal }) =>
      request(`/panels/anomalies?appId=${encodeURIComponent(appId)}&window=${encodeURIComponent(window)}`, { signal }),
    // Slice 2 — snooze/ack de anomalías (mutaciones: CSRF requerido).
    snoozeAnomaly: ({ appId, fingerprint, hours, csrf, signal }) =>
      request('/panels/anomalies/snooze', {
        method: 'POST',
        body: hours ? { appId, fingerprint, hours } : { appId, fingerprint },
        csrf,
        signal,
      }),
    ackAnomaly: ({ appId, fingerprint, csrf, signal }) =>
      request('/panels/anomalies/ack', {
        method: 'POST',
        body: { appId, fingerprint },
        csrf,
        signal,
      }),
    setAnomalySensitivity: ({ appId, sensitivity, csrf, signal }) =>
      request('/panels/anomalies/sensitivity', {
        method: 'POST',
        body: { appId, sensitivity },
        csrf,
        signal,
      }),
    // T001-2 / T001-3 — semáforo y disponibilidad. Fuera del gate
    // `requireInternalUser` en el backend: son los dos paneles que el
    // rol tenant_client puede ver en su portal.
    semaphore: ({ appId, window, signal }) =>
      request(`/panels/semaphore?appId=${encodeURIComponent(appId)}&window=${encodeURIComponent(window)}`, { signal }),
    uptime: ({ appId, window, signal }) =>
      request(`/panels/uptime?appId=${encodeURIComponent(appId)}&window=${encodeURIComponent(window)}`, { signal }),
    anomalyDetail: ({ appId, metric, pagePath, window, signal }) =>
      request(
        '/panels/anomalies/detail'
        + `?appId=${encodeURIComponent(appId)}`
        + `&metric=${encodeURIComponent(metric)}`
        + `&pagePath=${encodeURIComponent(pagePath)}`
        + `&window=${encodeURIComponent(window)}`,
        { signal },
      ),
    // F1 / U1.12 — agregado total del sistema, sin appId (agrega por
    // tenantScope del que pregunta). Mismo scope protegido que
    // semaphore/uptime: tenant_client también lo puede ver.
    system: ({ window, signal }) =>
      request(`/panels/system?window=${encodeURIComponent(window)}`, { signal }),
    // F1 / U1.10-U1.11 — grupos de error y correlación entre entornos.
    // Panel INTERNO (requireInternalUser en el backend): no expuesto al
    // portal de cliente.
    errorGroups: ({ appId, window, fingerprint, signal }) =>
      request(
        `/panels/errors?appId=${encodeURIComponent(appId)}`
        + (window ? `&window=${encodeURIComponent(window)}` : '')
        + (fingerprint ? `&fingerprint=${encodeURIComponent(fingerprint)}` : ''),
        { signal },
      ),
    errorDetails: ({ appId, fingerprint, window, cursor, signal }) =>
      request(`/panels/errors/${encodeURIComponent(fingerprint)}/details?appId=${encodeURIComponent(appId)}`
        + (window ? `&window=${encodeURIComponent(window)}` : '')
        + (cursor ? `&cursor=${encodeURIComponent(cursor)}` : ''), { signal }),
    errorEnvironments: ({ fingerprint, signal }) =>
      request(`/panels/errors/${encodeURIComponent(fingerprint)}/environments`, { signal }),
    incidentCandidates: ({ appId, signal }) =>
      request(`/panels/incidents?appId=${encodeURIComponent(appId)}`, { signal }),
    incidentDeliveries: ({ appId, candidateId, signal }) =>
      request(`/panels/incidents/${encodeURIComponent(candidateId)}/deliveries?appId=${encodeURIComponent(appId)}`, { signal }),
    // F2 / U2.10 — panel interno de estabilidad de los cambios (pipelines,
    // últimos despliegues por entorno, commits por módulo, correlación
    // con errores). Igual que errorGroups: nunca expuesto al portal de
    // cliente (requireInternalUser en el backend).
    delivery: ({ appId, window, signal }) =>
      request(
        `/panels/delivery?appId=${encodeURIComponent(appId)}`
        + (window ? `&window=${encodeURIComponent(window)}` : ''),
        { signal },
      ),
    // B-09 — resultados E2E persistidos y evidencia con URL breve.
    syntheticSummary: ({ appId, environment, window, signal }) =>
      request(
        `/panels/synthetic/summary?appId=${encodeURIComponent(appId)}`
        + (environment ? `&environment=${encodeURIComponent(environment)}` : '')
        + (window ? `&window=${encodeURIComponent(window)}` : ''),
        { signal },
      ),
    syntheticCharts: ({ appId, environment, window, signal }) =>
      request(
        `/panels/synthetic/charts?appId=${encodeURIComponent(appId)}`
        + (environment ? `&environment=${encodeURIComponent(environment)}` : '')
        + (window ? `&window=${encodeURIComponent(window)}` : ''),
        { signal },
      ),
    syntheticRuns: ({ appId, environment, window, page, limit, signal }) =>
      request(
        `/panels/synthetic/runs?appId=${encodeURIComponent(appId)}`
        + (environment ? `&environment=${encodeURIComponent(environment)}` : '')
        + (window ? `&window=${encodeURIComponent(window)}` : '')
        + (page ? `&page=${encodeURIComponent(page)}` : '')
        + (limit ? `&limit=${encodeURIComponent(limit)}` : ''),
        { signal },
      ),
    syntheticRun: ({ appId, runId, signal }) =>
      request(
        `/panels/synthetic/runs/${encodeURIComponent(runId)}?appId=${encodeURIComponent(appId)}`,
        { signal },
      ),
    syntheticArtifactUrl: ({ appId, artifactId, csrf, signal }) =>
      request(`/panels/synthetic/artifacts/${encodeURIComponent(artifactId)}/sign`, {
        method: 'POST', body: { appId }, csrf, signal,
      }),
    // B-04H — internal host telemetry. The API deliberately exposes a
    // source list and a series for one source; it never averages hosts.
    hostHealthSources: ({ appId, environment, staleAfterSeconds, signal }) =>
      request(
        `/panels/host-health/sources?appId=${encodeURIComponent(appId)}`
        + (environment ? `&environment=${encodeURIComponent(environment)}` : '')
        + (staleAfterSeconds ? `&staleAfterSeconds=${encodeURIComponent(staleAfterSeconds)}` : ''),
        { signal },
      ),
    hostHealthSeries: ({ appId, environment, sourceId, window, signal }) =>
      request(
        `/panels/host-health/series?appId=${encodeURIComponent(appId)}`
        + `&environment=${encodeURIComponent(environment)}`
        + `&sourceId=${encodeURIComponent(sourceId)}`
        + (window ? `&window=${encodeURIComponent(window)}` : ''),
        { signal },
      ),
    // OP-10 — Infra v2 identifies a host probe by its resource and probe,
    // avoiding the environment/sourceId ambiguity retained by v1.
    probeInfraSources: ({ appId, after, signal }) =>
      request(
        `/panels/probe-infra/sources?appId=${encodeURIComponent(appId)}`
        + (after ? `&after=${encodeURIComponent(after)}` : ''),
        { signal },
      ),
    probeInfraSeries: ({ appId, resourceId, probeId, window, signal }) =>
      request(
        `/panels/probe-infra/series?appId=${encodeURIComponent(appId)}`
        + `&resourceId=${encodeURIComponent(resourceId)}`
        + `&probeId=${encodeURIComponent(probeId)}`
        + (window ? `&window=${encodeURIComponent(window)}` : ''),
        { signal },
      ),
    probeInventory: ({ appId, environment, resourceId, probeId, family, after, limit = 100, signal }) => {
      const query = new URLSearchParams({limit: String(Math.min(Math.max(Number(limit) || 100, 1), 100))});
      for (const [name, value] of Object.entries({appId, environment, resourceId, probeId, family, after})) {
        if (value) query.set(name, value);
      }
      return request(`/probes/inventory?${query.toString()}`, { signal });
    },
    probeLogs: ({appId,environment,resourceId,sourceId,severity,window='24h',afterAt,afterId,limit=50,signal}) => {
      const query=new URLSearchParams({window,limit:String(Math.min(Math.max(Number(limit)||50,1),100))});
      for(const [name,value] of Object.entries({appId,environment,resourceId,sourceId,severity,afterAt,afterId})) {
        if(value) query.set(name,value);
      }
      return request(`/panels/probe-logs/events?${query.toString()}`,{signal});
    },
    probeHeartbeatTargets: ({appId,environment,targetId,observerId,window='24h',after,limit=50,signal}) => {
      const query=new URLSearchParams({window,limit:String(Math.min(Math.max(Number(limit)||50,1),100))});
      for(const [name,value] of Object.entries({appId,environment,targetId,observerId,after})) if(value) query.set(name,value);
      return request(`/panels/probe-heartbeat/targets?${query.toString()}`,{signal});
    },
    probeHeartbeatSeries: ({appId,environment,assignmentId,window='24h',signal}) => {
      const query=new URLSearchParams({assignmentId,window});
      if(appId) query.set('appId',appId);
      if(environment) query.set('environment',environment);
      return request(`/panels/probe-heartbeat/series?${query.toString()}`,{signal});
    },
  },

  // Heatmap drill-down (Stories 6.7, 6.8, 6.10). The signed URL is a
  // POST (CSRF protected) because issuing a presigned S3 URL is a
  // mutating-ish capability; the click density is a plain GET.
  // Both endpoints expect `appId` for the tenant scope gate; the
  // backend reads `req.scope.appId` rather than the body/query value
  // (defense in depth — Story 1.11).
  screenshots: {
    signUrl: ({ appId, pageUrlHash, csrf, signal } = {}) => request(
      '/heatmap/sign',
      { method: 'POST', body: { appId, pageUrlHash }, csrf, signal },
    ),
    clicks: ({ appId, pageUrlHash, window, signal } = {}) => request(
      '/heatmap/clicks'
      + `?appId=${encodeURIComponent(appId)}`
      + `&pageUrlHash=${encodeURIComponent(pageUrlHash)}`
      + `&window=${encodeURIComponent(window)}`,
      { signal },
    ),
  },

  // Admin Health page (Epic 3). Solo platform_admin; el backend gatea
  // con `requirePlatformAdmin` (Story 3.1), el frontend con `adminOnly`
  // en el router (defense-in-depth).
  admin: {
    errorIssue: {
      get: ({ appId, fingerprint, signal }) => request(
        `/admin/apps/${encodeURIComponent(appId)}/errors/${encodeURIComponent(fingerprint)}/issue`,
        { signal },
      ),
      submit: ({ appId, fingerprint, csrf, signal, ...body }) => request(
        `/admin/apps/${encodeURIComponent(appId)}/errors/${encodeURIComponent(fingerprint)}/issue`,
        { method: 'POST', body, csrf, signal },
      ),
    },
    splitErrorGroup: ({ appId, fingerprint, splitBy, csrf }) => request(
      `/admin/error-groups/${encodeURIComponent(fingerprint)}/split`,
      { method:'POST',body:{appId,splitBy},csrf }),
    confirmIncident: ({ candidateId, severity, csrf }) => request(
      `/admin/incidents/${encodeURIComponent(candidateId)}/confirm`,
      { method:'POST',body:{severity},csrf }),
    discardIncident: ({ candidateId, csrf }) => request(
      `/admin/incidents/${encodeURIComponent(candidateId)}/discard`,
      { method:'POST',body:{},csrf }),
    health: ({ signal } = {}) => request('/admin/health', { signal }),
    appsHealth: ({ signal } = {}) => request('/admin/health/apps', { signal }),
    // Story 6.9 AC1 — drill-down de storage por tenant. La tabla de
    // /admin/health/apps ya trae `screenshotBytes` agregado; este
    // endpoint expone el conteo exacto de capturas además del tamaño.
    tenantStorage: (appId, { signal } = {}) => request(
      `/admin/health/apps/${encodeURIComponent(appId)}/storage`,
      { signal },
    ),
    // Story 4.1 — apps CRUD list. Acepta `ifNoneMatch` opcional para
    // budget-saver: si el server matchea, devuelve 304 con body=null y
    // el caller usa la última copia.
    apps: {
      list: ({ signal, ifNoneMatch } = {}) => requestWithMeta('/admin/apps', {
        signal,
        headers: ifNoneMatch ? { 'If-None-Match': ifNoneMatch } : {}
      }),
      // Story 4.4 — POST /admin/apps. CSRF requerido (mutating).
      create: (payload, { csrf, signal } = {}) => request('/admin/apps', {
        method: 'POST',
        body: payload,
        csrf,
        signal,
      }),
      // Story 4.7/4.8 — PATCH /admin/apps/:appId con If-Match.
      // ifMatchVersion: integer del list endpoint (resp.version).
      // 200 → { ok, app, version }; 409 → ApiError code "version_conflict"
      // con details.current_version + details.current via el envelope
      // top-level (parseado por requestWithMeta).
      update: (appId, patch, ifMatchVersion, { csrf, signal } = {}) => request(
        `/admin/apps/${encodeURIComponent(appId)}`,
        {
          method: 'PATCH',
          body: patch,
          csrf,
          signal,
          headers: { 'If-Match': String(ifMatchVersion) },
        },
      ),
      // Story 4.9 — POST /admin/apps/:appId/rotate-key. Sin body.
      // 200 → { ok, apiKey: <plaintext-once>, fingerprint, prevValidUntil }.
      // CSRF requerido (mutating).
      rotateKey: (appId, { csrf, signal } = {}) => request(
        `/admin/apps/${encodeURIComponent(appId)}/rotate-key`,
        {
          method: 'POST',
          // body undefined → no Content-Type, no JSON body.
          csrf,
          signal,
        },
      ),
      // F1 — POST /admin/apps/:appId/rotate-server-key.
      rotateServerKey: (appId, { csrf, signal } = {}) => request(
        `/admin/apps/${encodeURIComponent(appId)}/rotate-server-key`,
        {
          method: 'POST',
          csrf,
          signal,
        },
      ),
      // F2 — asociación de la app con su proyecto GitLab. PUT reemplaza
      // la configuración completa; no se envía ningún token al navegador.
      codeProject: {
        get: (appId, { signal } = {}) => request(
          `/admin/apps/${encodeURIComponent(appId)}/code-project`,
          { signal },
        ),
        save: (appId, payload, { csrf, signal } = {}) => request(
          `/admin/apps/${encodeURIComponent(appId)}/code-project`,
          { method: 'PUT', body: payload, csrf, signal },
        ),
        testIssues: (appId, { csrf, signal } = {}) => request(
          `/admin/apps/${encodeURIComponent(appId)}/code-project/test-issues`,
          { method: 'POST', body: {}, csrf, signal },
        ),
      },
      vcsHealth: (appId, { signal } = {}) => request(
        `/admin/apps/${encodeURIComponent(appId)}/vcs-health`,
        { signal },
      ),
      runVcsAction: (appId, action, { csrf, signal } = {}) => request(
        `/admin/apps/${encodeURIComponent(appId)}/vcs-${encodeURIComponent(action)}`,
        { method: 'POST', csrf, signal },
      )
    },
    // T001-4 — CRUD de CLIENTES (`tenants`). Ojo con el vocabulario:
    // `admin.apps` es el CRUD de aplicaciones; esto es el cliente dueño
    // de esas apps y de los usuarios.
    clients: {
      list: ({ signal } = {}) => request('/admin/tenants', { signal }),
      create: (payload, { csrf, signal } = {}) => request('/admin/tenants', {
        method: 'POST',
        body: payload,
        csrf,
        signal,
      }),
      update: (id, patch, { csrf, signal } = {}) => request(
        `/admin/tenants/${encodeURIComponent(id)}`,
        { method: 'PATCH', body: patch, csrf, signal },
      ),
    },
    // Story 4.12 / 4.14 — audit log read. Offset pagination + orden por
    // columna + filtros opcionales. El backend gatea con
    // `requirePlatformAdmin` (FR27). `filters` keys: actor, action,
    // target, from, to — todas opcionales. `sort` ∈ ts|actor|action|
    // target|id, `dir` ∈ asc|desc.
    audit: ({ filters = {}, offset = 0, limit, sort, dir, signal } = {}) => {
      const params = new URLSearchParams();
      for (const k of ['actor', 'action', 'target', 'from', 'to']) {
        if (filters[k]) params.set(k, filters[k]);
      }
      if (offset) params.set('offset', String(offset));
      if (limit) params.set('limit', String(limit));
      if (sort) params.set('sort', sort);
      if (dir) params.set('dir', dir);
      const qs = params.toString();
      return request(`/admin/audit${qs ? `?${qs}` : ''}`, { signal });
    },
    // Story 5.6 — admin users list / detail / manual reset password.
    // CRUD: create / update (role + scope) / remove. Todas las
    // mutations requieren CSRF.
    users: {
      // GET /admin/users — list. `filters` keys (todas opcionales): email
      // (prefix), role (exact), scope ∈ all|scoped|none. Orden opcional:
      // `sort` ∈ email|role|createdAt, `dir` ∈ asc|desc.
      list: ({ filters = {}, sort, dir, signal } = {}) => {
        const params = new URLSearchParams();
        for (const k of ['email', 'role', 'scope']) {
          if (filters[k]) params.set(k, filters[k]);
        }
        if (sort) params.set('sort', sort);
        if (dir) params.set('dir', dir);
        const qs = params.toString();
        return request(`/admin/users${qs ? `?${qs}` : ''}`, { signal });
      },
      detail: (userId, { signal } = {}) => request(
        `/admin/users/${encodeURIComponent(userId)}`,
        { signal },
      ),
      // POST /admin/users — crea un usuario; 201 → { ok, user, temporaryPassword }.
      create: (payload, { csrf, signal } = {}) => request('/admin/users', {
        method: 'POST',
        body: payload,
        csrf,
        signal,
      }),
      // PATCH /admin/users/:userId — edita role + tenantScope.
      update: (userId, patch, { csrf, signal } = {}) => request(
        `/admin/users/${encodeURIComponent(userId)}`,
        { method: 'PATCH', body: patch, csrf, signal },
      ),
      // DELETE /admin/users/:userId.
      remove: (userId, { csrf, signal } = {}) => request(
        `/admin/users/${encodeURIComponent(userId)}`,
        { method: 'DELETE', csrf, signal },
      ),
      // POST /reset-password — sin body. CSRF requerido (mutating).
      resetPassword: (userId, { csrf, signal } = {}) => request(
        `/admin/users/${encodeURIComponent(userId)}/reset-password`,
        { method: 'POST', csrf, signal },
      ),
    }
  },

  // Perfil del usuario logueado (cualquier role). El backend gatea con
  // sesión válida (no admin).
  user: {
    profile: ({ signal } = {}) => request('/user/profile', { signal }),
    // POST /user/change-password — { currentPassword, newPassword }.
    changePassword: (body, { csrf, signal } = {}) => request('/user/change-password', {
      method: 'POST',
      body,
      csrf,
      signal,
    }),
  }
};

export { ApiError };
